(() => {
  'use strict';

  const ROOT = window.Shopify?.routes?.root || '/';
  const LOOP_SECONDS = 3 * 60 * 60;

  function digits(value) {
    const text = String(value);
    if ((document.documentElement.lang || '').toLowerCase().startsWith('ar')) {
      return text.replace(/[0-9]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'[Number(digit)]);
    }
    return text;
  }

  function pad(value) { return digits(String(Math.max(0, value)).padStart(2, '0')); }
  function saleIsActive(root) { const compare = root.querySelector('[data-nawah-compare-price]'); return Boolean(compare && !compare.hidden && compare.textContent.trim()); }
  function loopRemainingSeconds() { const nowSeconds = Math.floor(Date.now() / 1000); const elapsed = ((nowSeconds % LOOP_SECONDS) + LOOP_SECONDS) % LOOP_SECONDS; return elapsed === 0 ? LOOP_SECONDS : LOOP_SECONDS - elapsed; }

  function buildCountdown(root, config) {
    if (config.dataset.countdownEnabled !== 'true') return;
    const price = root.querySelector('.nawah-product__price');
    if (!price || root.querySelector('[data-nawah-sale-countdown]')) return;
    const node = document.createElement('section');
    node.className = 'nawah-sale-countdown';
    node.setAttribute('data-nawah-sale-countdown', 'true');
    node.innerHTML = `<div class="nawah-sale-countdown__copy"><span>${config.dataset.countdownBadge || 'Limited-time offer'}</span><strong>${config.dataset.countdownTitle || 'Offer ends in'}</strong></div><div class="nawah-sale-countdown__clock" aria-label="${config.dataset.countdownTitle || 'Offer ends in'}"><span><b data-nawah-timer-hours>00</b><small>${config.dataset.hoursLabel || 'Hours'}</small></span><i>:</i><span><b data-nawah-timer-minutes>00</b><small>${config.dataset.minutesLabel || 'Min'}</small></span><i>:</i><span><b data-nawah-timer-seconds>00</b><small>${config.dataset.secondsLabel || 'Sec'}</small></span></div>`;
    price.insertAdjacentElement('afterend', node);
    const hours = node.querySelector('[data-nawah-timer-hours]');
    const minutes = node.querySelector('[data-nawah-timer-minutes]');
    const seconds = node.querySelector('[data-nawah-timer-seconds]');
    function tick() {
      if (!saleIsActive(root)) { node.hidden = true; return; }
      node.hidden = false;
      const totalSeconds = loopRemainingSeconds();
      hours.textContent = pad(Math.floor(totalSeconds / 3600));
      minutes.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
      seconds.textContent = pad(totalSeconds % 60);
    }
    const compare = root.querySelector('[data-nawah-compare-price]');
    if (compare) new MutationObserver(tick).observe(compare, { attributes: true, childList: true, subtree: true });
    tick();
    window.setInterval(tick, 1000);
  }

  function waitForCartUpdate(timeout = 12000) {
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => { document.removeEventListener('nawah:cart-updated', onUpdate); reject(new Error('Timed out while adding the selected offer.')); }, timeout);
      function onUpdate(event) { window.clearTimeout(timer); resolve(event.detail?.cart || null); }
      document.addEventListener('nawah:cart-updated', onUpdate, { once: true });
    });
  }

  async function addStandardProduct(form) {
    const variantInput = form.querySelector('[data-nawah-variant-id]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const variantId = Number(variantInput?.value || 0);
    const quantity = Math.max(1, Number(quantityInput?.value || 1));
    if (!Number.isFinite(variantId) || variantId <= 0) throw new Error('No purchasable variant is selected.');
    const response = await fetch(`${ROOT}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin', body: JSON.stringify({ items: [{ id: variantId, quantity }] }) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || 'Unable to add this item.');
    return data;
  }

  async function continueToCheckoutWithShippingGate() {
    const checkoutUrl = `${ROOT}checkout`;
    if (window.NawahShippingUpsell?.gateCheckout) {
      const shouldContinue = await window.NawahShippingUpsell.gateCheckout(checkoutUrl);
      if (!shouldContinue) return false;
    }
    window.location.assign(checkoutUrl);
    return true;
  }

  function buildBuyNow(root, config) {
    const form = root.querySelector('form[data-nawah-product-form]');
    if (!form || form.querySelector('[data-nawah-buy-now]')) return;
    const purchase = form.querySelector('.nawah-product__purchase');
    const addButton = form.querySelector('[data-nawah-add]');
    if (!purchase || !addButton) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'nawah-product__buy-now';
    button.setAttribute('data-nawah-buy-now', 'true');
    button.innerHTML = `<span data-nawah-buy-now-label>${config.dataset.buyNowLabel || 'Buy now'}</span><small>${config.dataset.buyNowNote || 'Go straight to checkout'}</small>`;
    purchase.insertAdjacentElement('afterend', button);
    const label = button.querySelector('[data-nawah-buy-now-label]');
    const status = form.querySelector('[data-nawah-status]');
    const defaultLabel = config.dataset.buyNowLabel || 'Buy now';
    function syncAvailability() { button.disabled = Boolean(addButton.disabled); }
    new MutationObserver(syncAvailability).observe(addButton, { attributes: true, attributeFilter: ['disabled'] });
    syncAvailability();
    button.addEventListener('click', async () => {
      if (button.disabled) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      label.textContent = config.dataset.buyNowBusy || 'Preparing checkout…';
      if (status) status.textContent = '';
      try {
        const choiceOffer = form.querySelector('[data-body-splash-offer]');
        if (choiceOffer) { const update = waitForCartUpdate(); addButton.click(); await update; }
        else { await addStandardProduct(form); }
        if (window.NawahGiftEngine?.sync) await window.NawahGiftEngine.sync();
        const checkoutStarted = await continueToCheckoutWithShippingGate();
        if (!checkoutStarted) { button.removeAttribute('aria-busy'); label.textContent = defaultLabel; syncAvailability(); }
      } catch (error) {
        console.error('NAWAH Buy Now failed:', error);
        if (status) status.textContent = config.dataset.buyNowError || 'Could not continue to checkout. Please try again.';
        button.removeAttribute('aria-busy');
        label.textContent = defaultLabel;
        syncAvailability();
      }
    });
  }

  function replaceNewBundleSummary(root, config) {
    if (config.dataset.isNewBundle !== 'true') return;
    const template = document.querySelector('template[data-nawah-new-bundle-summary-template]');
    if (!template) return;
    const replacement = template.content.firstElementChild?.cloneNode(true);
    if (!replacement) return;
    const current = root.querySelector('[data-bundle-summary]');
    if (current) current.replaceWith(replacement);
    else root.querySelector('form[data-nawah-product-form]')?.insertAdjacentElement('beforebegin', replacement);
  }

  function init() {
    const config = document.querySelector('[data-nawah-pdp-enhancements-config]');
    const root = document.querySelector('[data-nawah-product]');
    if (!config || !root || root.dataset.nawahPdpEnhancementsReady === 'true') return;
    root.dataset.nawahPdpEnhancementsReady = 'true';
    replaceNewBundleSummary(root, config);
    buildCountdown(root, config);
    buildBuyNow(root, config);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
